import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@outschool/backpack";
import { faComment, faPlus, faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import React from "react";

import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadListItem from "./ThreadListItem";
import { ThreadItem } from "./types";
import { isGeneralThread } from "./utils";

interface ThreadListBodyProps {
  threads: ThreadItem[];
  threadsLoading: boolean;
  showEmptyConversationsState: boolean;
  teacherName: string;
  debouncedSearch: string;
  onThreadClick: (threadUid: string) => void;
  onOpenGeneralThread: () => void;
}

function ThreadListBody({
  threads,
  threadsLoading,
  showEmptyConversationsState,
  teacherName,
  debouncedSearch,
  onThreadClick,
  onOpenGeneralThread,
}: ThreadListBodyProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListView",
  );

  if (threadsLoading && threads.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          p: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (showEmptyConversationsState) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 24,
          gap: 12,
        }}
      >
        <Icon
          icon={faComment}
          sx={{ color: "neutral.300", fontSize: "4rem" }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("No conversations yet")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", maxWidth: 300 }}
        >
          {t(
            'Explore a lesson and use the "Ask a question" button below your lesson to start chatting with your teacher.',
          )}
        </Typography>
        <Button
          variant="outlined"
          onClick={onOpenGeneralThread}
          sx={{
            mt: 8,
            borderColor: "neutral.200",
            color: "text.primary",
            borderRadius: "1.4rem",
            textTransform: "none",
          }}
        >
          {t("Message {{teacherName}} directly", {
            teacherName,
          })}
        </Button>
      </Box>
    );
  }

  if (threads.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {t("No messages found")}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {threads.map(thread => (
        <ThreadListItem
          key={thread.uid}
          thread={thread}
          teacherName={teacherName}
          onClick={() => onThreadClick(thread.uid)}
        />
      ))}
      {!debouncedSearch && !threads.some(isGeneralThread) && (
        <Box
          data-test-id="ask-anything-row"
          onClick={onOpenGeneralThread}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            py: 12,
            px: 16,
            borderBottom: "1px solid",
            borderColor: "divider",
            cursor: "pointer",
            transition: "background-color 0.15s",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "primary.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon
              icon={faPlus}
              sx={{ color: "primary.main", fontSize: "1.2rem" }}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            {t("Ask {{teacherName}} anything", { teacherName })}
          </Typography>
        </Box>
      )}
    </>
  );
}

interface ThreadListViewProps {
  onThreadClick: (threadUid: string) => void;
  onOpenGeneralThread: () => void;
  onClose: () => void;
}

export default function ThreadListView({
  onThreadClick,
  onOpenGeneralThread,
  onClose,
}: ThreadListViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\ThreadListView",
  );
  const {
    leader,
    section,
    teacherName,
    threads,
    threadsLoading,
    searchInput,
    debouncedSearch,
    setSearchInput,
  } = useMessageTeacherDrawerContext();
  const showEmptyConversationsState =
    !threadsLoading && threads.length === 0 && !debouncedSearch;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          p: 12,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <UserHeadshotImage user={leader} size={44} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
            {teacherName}
          </Typography>
          {Boolean(section.end_time) && (
            <Typography variant="caption" color="text.secondary">
              {t("Teacher access ends {{date}}", {
                date: dayjs(section.end_time).format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={t("Close messages")}
          icon={faTimes}
          variant="text"
          onClick={onClose}
        />
      </Box>
      {!showEmptyConversationsState && (
        <Box
          sx={{
            py: 8,
            px: 12,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder={t("Search all messages")}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon
                    icon={faSearch}
                    sx={{ color: "text.secondary", fontSize: "1rem" }}
                  />
                </InputAdornment>
              ),
              endAdornment:
                threadsLoading && debouncedSearch ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : undefined,
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 20 },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
        }}
      >
        <ThreadListBody
          threads={threads}
          threadsLoading={threadsLoading}
          showEmptyConversationsState={showEmptyConversationsState}
          teacherName={teacherName}
          debouncedSearch={debouncedSearch}
          onThreadClick={onThreadClick}
          onOpenGeneralThread={onOpenGeneralThread}
        />
      </Box>
    </>
  );
}
